<!--
  ExtensionManage.vue
  콜봇 내선 관리 페이지(/callbot/extensions).

  두 화면이 한 라우트에 산다.
  - `?companyId` 없음: 병원 목록(검색·페이지네이션). 상담톡 관리와 같은 "목록 먼저" 구조.
    원천은 call-api 콜봇 조직 목록(콜봇을 쓰는 병원만, GET /api/v1/callbot/organizations —
    ~/md/back/콜봇-내선관리-조직목록-CERTILIFE-백엔드요청.md). 그 라우트가 미배포(404)면 inapi 전체
    병원 목록으로 폴백하고 그 사실을 표에 알린다. 목록 상태(`q`·`cat`·`page`·`size`)도 쿼리에 둬서
    병원에서 돌아와도 같은 페이지가 보인다.
  - `?companyId=<companies._id>`: 그 병원의 내선·전화기·설치 장소 sub-tab(`?sub=`). studio
    `AgentExtensionsView.vue` 를 옮긴 부분이다. 목록은 호스트가 한 번 받아(usePbxInventory) 세
    패널이 조인한다.

  병원 이름은 행을 고른 시점에 sessionStorage 에 두고, 딥링크로 들어와 이름이 없으면 id 만 보인다
  (inapi 단건 조회는 부르지 않는다). 병원이 바뀌면 inventory 가 네 목록·4·7단계 판정을 비우고
  다시 받고, 패널·상세·모달·1회 표시 값(URL·비밀번호·복구 코드)은 `:key="companyId"` 로 통째로
  다시 마운트해 지운다.

  계약: ~/md/front/admin-콜봇-내선관리-화면-요청.md "요청 계약 2·3"(병원 선택 UI 는 사용자 결정으로
        드롭다운 → 목록 우선으로 변경, 2026-09-28), ~/md/front/studio-내선·전화기·사이트-탭-요청.md
-->
<template>
  <div class="container-fluid container-wrap col-sm-12 col-xs-12">
    <div style="margin-top: 28px"></div>

    <!-- 페이지 헤더 영역 -->
    <div class="card-header pb-0 col-sm-12 d-flex">
      <div class="col-sm-12 col-xs-12">
        <div class="col-sm-12 col-xs-12 justify-content-between titleContainer">
          <div class="title-row">
            <h1 class="fw-bold page-title">내선 관리</h1>
            <div class="header-flags">
              <span v-if="companyId && inventory.polling.value" class="ext-view__polling">자동 갱신 중</span>
              <span
                v-if="inventory.mock"
                class="ext-view__mock"
                title="DEV ?pbxMock=1 — 서버에 요청하지 않습니다"
              >
                목 데이터
              </span>
            </div>
          </div>
          <span class="pageDesc">
            병원의 콜봇 내선과 전화기, 전화기 설치 장소를 관리합니다. 전화기 연결은 운영팀이 진행합니다.
          </span>
        </div>
      </div>
    </div>

    <!-- 콘텐츠 영역 -->
    <div class="px-0 pb-2">
      <div class="card content-wrap-card">
        <!-- 1) 병원 목록 — 고르기 전 -->
        <HospitalPickerTable
          v-if="!companyId"
          :mode="listMode"
          :notice="listNotice"
          :rows="hospitalRows"
          :loading="hospitalLoading"
          :search="listQuery.search"
          :condition="listQuery.condition"
          :page="listQuery.page"
          :page-size="listQuery.pageSize"
          :total="hospitalTotal"
          @select="selectHospital"
          @search="onHospitalSearch"
          @page="onHospitalPage"
        />

        <!-- 2) 선택한 병원의 내선 관리 -->
        <div v-else class="ext-view">
          <div class="ext-view__company">
            <a-button class="ext-view__back" @click="backToList">
              <i class="fa-solid fa-arrow-left"></i>
              병원 목록
            </a-button>
            <div class="ext-view__company-name">
              <span class="ext-view__company-label">병원</span>
              <strong>{{ companyLabel }}</strong>
              <span v-if="companyLabel === companyId" class="ext-view__company-hint">
                이름은 목록에서 고르면 표시됩니다
              </span>
            </div>
          </div>

          <nav class="ext-view__tabs" role="tablist" aria-label="내선 관리">
            <button
              v-for="t in SUB_TABS"
              :key="t.key"
              type="button"
              role="tab"
              class="ext-view__tab"
              :class="{ 'is-active': sub === t.key }"
              :aria-selected="sub === t.key"
              @click="switchSub(t.key)"
            >
              {{ t.label }}
            </button>
          </nav>

          <!-- 병원이 바뀌면 패널·상세·모달·1회 표시 값을 통째로 다시 마운트해 지운다 -->
          <div :key="companyId" class="ext-view__body">
            <PbxExtensionsPanel v-if="sub === 'extensions'" :inventory="inventory" />
            <template v-else>
              <p v-if="inventory.stage7.value === false" class="ext-view__unavailable">
                {{ STAGE7_UNAVAILABLE_HINT }}
              </p>
              <PbxDevicesPanel v-else-if="sub === 'devices'" :inventory="inventory" />
              <PbxSitesPanel v-else :inventory="inventory" />
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ExtensionManage',
};
</script>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { message } from 'ant-design-vue';

import HospitalPickerTable from './components/HospitalPickerTable.vue';
import PbxDevicesPanel from './components/PbxDevicesPanel.vue';
import PbxExtensionsPanel from './components/PbxExtensionsPanel.vue';
import PbxSitesPanel from './components/PbxSitesPanel.vue';
import { callbotApi } from './api';
import { usePbxInventory } from '@/views/Callbot/service/usePbxInventory';
import { isCallApiUnimplemented } from '@/views/Callbot/service/callApiError';
import { getPbxApi } from '@/views/Callbot/service/pbxProvisioningMock';
import { STAGE7_UNAVAILABLE_HINT, compareLabel } from '@/views/Callbot/service/pbxExtensions';

/** 선택한 병원 이름 캐시(sessionStorage, JSON `{ [id]: name }`) — 딥링크 라벨용 */
const COMPANY_NAMES_KEY = 'admin.pbxCompanyNames';

const SUB_TABS = [
  { key: 'extensions', label: '내선' },
  { key: 'devices', label: '전화기' },
  { key: 'sites', label: '설치 장소' },
];

const SEARCH_CATEGORIES = ['name', 'cNo', 'adminName'];
const PAGE_SIZES = [10, 20, 50];

const route = useRoute();
const router = useRouter();

function queryString(key) {
  const v = route.query[key];
  const s = Array.isArray(v) ? v[0] : v;
  return typeof s === 'string' ? s : '';
}

// ---------------------------------------------------------------------------
// 선택 병원 — 원천은 ?companyId= 쿼리
// ---------------------------------------------------------------------------
const companyId = computed(() => queryString('companyId') || null);

const inventory = usePbxInventory(companyId);

function readCompanyNames() {
  try {
    const raw = sessionStorage.getItem(COMPANY_NAMES_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function rememberCompanyName(id, name) {
  if (!id || !name) return;
  try {
    const names = readCompanyNames();
    names[id] = name;
    sessionStorage.setItem(COMPANY_NAMES_KEY, JSON.stringify(names));
  } catch {
    // 저장 못 해도 화면은 id 라벨로 돈다
  }
}

// 이름은 캐시에서만 읽는다 — 딥링크로 들어와 없으면 id 가 그대로 라벨이다
const companyLabel = computed(() => {
  const id = companyId.value;
  if (!id) return '';
  const name = readCompanyNames()[id];
  return typeof name === 'string' && name ? name : id;
});

// ---------------------------------------------------------------------------
// 병원 목록 — 검색·페이지 상태도 쿼리(q·cat·page·size)에 둔다
// ---------------------------------------------------------------------------
const listQuery = computed(() => {
  const cat = queryString('cat');
  const page = parseInt(queryString('page'), 10);
  const size = parseInt(queryString('size'), 10);
  return {
    search: SEARCH_CATEGORIES.includes(cat) ? cat : 'name',
    condition: queryString('q'),
    page: Number.isFinite(page) && page > 0 ? page : 1,
    pageSize: PAGE_SIZES.includes(size) ? size : 10,
  };
});

// 원천 1: call-api 콜봇 조직 목록(전체를 한 번에 받고 화면에서 검색·페이지를 나눈다)
// 원천 2(폴백): inapi 전체 병원 목록(서버 검색·페이지) — 조직 목록 라우트가 404 일 때만
const listSource = ref(null); // null = 아직 판정 전 | 'organizations' | 'hospitals'
const orgRows = ref([]);
const serverRows = ref([]);
const serverTotal = ref(0);
const hospitalLoading = ref(false);
// 늦게 온 앞 응답이 새 응답을 덮지 않게 — 마지막 요청만 반영
let hospitalSeq = 0;

const listMode = computed(() => (listSource.value === 'hospitals' ? 'hospitals' : 'organizations'));
const listNotice = computed(() =>
  listSource.value === 'hospitals'
    ? 'call-api 콜봇 조직 목록이 이 환경에 아직 배포되지 않아 전체 병원을 보입니다. 콜봇 조직이 없는 병원은 내선이 비어 있습니다.'
    : ''
);

// 조직 목록은 화면에서 이름 검색·정렬·페이지를 나눈다
const orgFiltered = computed(() => {
  const q = listQuery.value.condition.trim().toLowerCase();
  const rows = q ? orgRows.value.filter((r) => r.name.toLowerCase().includes(q) || r._id === q) : orgRows.value;
  return [...rows].sort((a, b) => compareLabel(a.nameMissing ? '' : a.name, b.nameMissing ? '' : b.name));
});
const hospitalTotal = computed(() => (listMode.value === 'organizations' ? orgFiltered.value.length : serverTotal.value));
const hospitalRows = computed(() => {
  const { page, pageSize } = listQuery.value;
  if (listMode.value !== 'organizations') return serverRows.value;
  const start = (page - 1) * pageSize;
  return orgFiltered.value.slice(start, start + pageSize).map((row, i) => ({ ...row, index: start + i + 1 }));
});

const listErrorMessage = (error, fallback) => {
  const msg = error?.response?.data?.message || error?.response?.data?.error?.message;
  if (Array.isArray(msg)) return msg.join(' / ') || fallback;
  return typeof msg === 'string' && msg ? msg : fallback;
};

/** call-api 조직 목록 → 표 행. 이름이 없으면 id 를 보이고 표시만 흐리게 */
function toOrgRow(item) {
  const id = String(item.company_id || '');
  const name = typeof item.company_name === 'string' ? item.company_name.trim() : '';
  return {
    _id: id,
    name: name || id,
    nameMissing: !name,
    keyStatus: item.key_status || 'pending',
    verificationStatus: item.verification_status || null,
    createdAt: typeof item.created_at === 'number' ? item.created_at : null,
  };
}

async function loadOrganizations() {
  const my = ++hospitalSeq;
  hospitalLoading.value = true;
  try {
    // ?pbxMock=1 이면 목 — 병원 목록도 서버 없이 볼 수 있게
    const res = await getPbxApi().listCallbotOrganizations();
    if (my !== hospitalSeq) return true;
    orgRows.value = (Array.isArray(res?.items) ? res.items : []).map(toOrgRow).filter((r) => r._id);
    listSource.value = 'organizations';
    return true;
  } catch (error) {
    if (my !== hospitalSeq) return true;
    if (isCallApiUnimplemented(error)) {
      // 라우트 미배포 — 전체 병원 목록으로 폴백(표 상단에 알린다)
      listSource.value = 'hospitals';
      return false;
    }
    message.error(listErrorMessage(error, '콜봇 조직 목록을 불러오지 못했습니다.'));
    orgRows.value = [];
    listSource.value = 'organizations';
    return true;
  } finally {
    if (my === hospitalSeq) hospitalLoading.value = false;
  }
}

async function loadHospitalsFromInapi() {
  const my = ++hospitalSeq;
  const { search, condition, page, pageSize } = listQuery.value;
  hospitalLoading.value = true;
  try {
    const res = await callbotApi.listHospitals({ page, limit: pageSize, search, condition });
    if (my !== hospitalSeq) return;
    const data = res.data || {};
    const docs = Array.isArray(data.docs) ? data.docs : [];
    const total = Number(data.totalDocs) || 0;
    // 번호는 병원 관리 화면과 같이 전체 기준 내림차순
    serverRows.value = docs.map((doc, i) => ({
      ...doc,
      index: total - i - (page - 1) * pageSize,
    }));
    serverTotal.value = total;
  } catch (error) {
    if (my !== hospitalSeq) return;
    message.error(listErrorMessage(error, '병원 목록을 불러오지 못했습니다.'));
    serverRows.value = [];
    serverTotal.value = 0;
  } finally {
    if (my === hospitalSeq) hospitalLoading.value = false;
  }
}

/** 목록 화면 진입·검색·페이지 변경 때. 조직 목록은 한 번 받으면 화면에서 나누므로 다시 받지 않는다 */
async function loadHospitals() {
  if (listSource.value === 'organizations') return;
  if (listSource.value === null) {
    const ok = await loadOrganizations();
    if (ok) return;
  }
  await loadHospitalsFromInapi();
}

function replaceListQuery(patch) {
  const query = { ...route.query };
  for (const [k, v] of Object.entries(patch)) {
    if (v === undefined || v === null || v === '') delete query[k];
    else query[k] = String(v);
  }
  void router.replace({ query });
}

function onHospitalSearch({ search, condition }) {
  // 검색 조건이 바뀌면 1페이지부터
  replaceListQuery({ cat: search === 'name' ? undefined : search, q: condition, page: undefined });
}

function onHospitalPage({ page, pageSize }) {
  const sizeChanged = pageSize !== listQuery.value.pageSize;
  replaceListQuery({
    page: sizeChanged || page === 1 ? undefined : page,
    size: pageSize === 10 ? undefined : pageSize,
  });
}

// 목록 → 병원. push 라서 뒤로 가기가 목록(같은 페이지·검색어)으로 돌아온다
function selectHospital(record) {
  const id = record?._id ? String(record._id) : '';
  if (!id) return;
  rememberCompanyName(id, typeof record.name === 'string' ? record.name : '');
  void router.push({ query: { ...route.query, companyId: id, sub: undefined } });
}

function backToList() {
  const query = { ...route.query };
  delete query.companyId;
  delete query.sub;
  void router.push({ query });
}

// 목록 화면에 있는 동안 검색·페이지 쿼리가 바뀌면 다시 받는다(딥링크 첫 진입 포함)
watch(
  () => [companyId.value, listQuery.value.search, listQuery.value.condition, listQuery.value.page, listQuery.value.pageSize],
  ([id]) => {
    if (!id) void loadHospitals();
  },
  { immediate: true }
);

// ---------------------------------------------------------------------------
// sub-tab — ?sub= 딥링크. 다른 query(companyId·목록 상태 포함)는 보존한다
// ---------------------------------------------------------------------------
const sub = computed(() => {
  if (route.query.sub === 'devices') return 'devices';
  if (route.query.sub === 'sites') return 'sites';
  return 'extensions';
});

function switchSub(next) {
  if (sub.value === next) return;
  void router.replace({ query: { ...route.query, sub: next === 'extensions' ? undefined : next } });
}

// 전화기 sub-tab 이 열려 있으면 폴링 조건이 된다 — inventory 에 현재 탭을 알린다
watch(sub, (s) => inventory.setActiveSub(s), { immediate: true });

// 첫 로드는 여기서 한 번만 — companyId 변화에 따른 로드는 inventory 안 watch(immediate 아님)가 한다
onMounted(() => {
  if (companyId.value) void inventory.loadAll();
});
</script>

<style scoped lang="scss">
.container-wrap {
  padding: 0 16px;
}

.titleContainer {
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
}

.title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 16px;
}

.page-title {
  color: #000;
  font-size: 1.8rem;
  margin-bottom: 0;
}

.pageDesc {
  color: #6c757d;
  font-size: 14px;
  margin-top: 8px;
}

.header-flags {
  display: flex;
  align-items: center;
  gap: 8px;
}

.content-wrap-card {
  padding: 16px;
}

.ext-view {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;

  &__mock {
    padding: 2px 8px;
    border-radius: 6px;
    background: #fff7e6;
    color: #d46b08;
    font-size: 12px;
    font-weight: 600;
  }

  &__polling {
    font-size: 12px;
    color: #8c8c8c;
  }

  &__company {
    display: flex;
    align-items: center;
    gap: 16px;
    flex: none;
    padding: 12px 16px;
    background: #fafafa;
    border-radius: 8px;
  }

  &__back {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  &__company-name {
    display: flex;
    align-items: baseline;
    gap: 8px;
    font-size: 15px;
    color: #262626;
  }

  &__company-label {
    font-size: 13px;
    font-weight: 600;
    color: #8c8c8c;
  }

  &__company-hint {
    font-size: 12px;
    color: #8c8c8c;
  }

  &__tabs {
    display: flex;
    gap: 4px;
    flex: none;
  }

  &__tab {
    padding: 8px 16px;
    border: 1px solid #f0f0f0;
    border-radius: 10px;
    background: #fff;
    color: #595959;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;

    &:hover {
      color: #262626;
    }

    &.is-active {
      border-color: #1668dc;
      background: #f0f5ff;
      color: #1668dc;
      font-weight: 600;
    }
  }

  &__body {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  &__unavailable {
    margin: 0;
    padding: 24px;
    border: 1px solid #f0f0f0;
    border-radius: 12px;
    background: #fff;
    color: #8c8c8c;
    font-size: 13px;
  }
}
</style>
